import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  SkeletonText,
  Text,
} from "@chakra-ui/react";
import { brandPalette } from "../../../constants/brandPalette";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import {
  FaBookOpen,
  FaFilePdf,
  FaQuestionCircle,
  FaRegFileAlt,
  FaEyeSlash,
  FaUserCircle,
  FaVideo,
  FaTags,
} from "react-icons/fa";
import { RiLogoutBoxRLine, RiPrinterFill, RiSettings4Line } from "react-icons/ri";
import { logoutUserAction } from "../../../modules/auth/Actions";
import { BsPeople, BsPersonX } from "react-icons/bs";
import { AiOutlineGroup } from "react-icons/ai";
import { LuDollarSign } from "react-icons/lu";
import {
  MdAccessTime,
  MdOutlineAssessment,
  MdOutlineAssignment,
  MdQuiz,
} from "react-icons/md";
import { BiPhone } from "react-icons/bi";
import AdminNotificationsBell from "../Notifications/AdminNotificationsBell";
import ThemeToggleButton from "../ThemeToggleButton";

const TeacherSidenavbar = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.user.isLoading);
  const location = useLocation();

  const palette = {
    primary: brandPalette.primary,
    secondary: brandPalette.secondary,
    tertiary: brandPalette.stone,
    neutralText: "rgba(247, 242, 236, 0.86)",
  };

  const sections = [
    {
      title: "Users",
      items: [
        { label: "Students", to: "/dashboard/students", icon: BsPeople },
        { label: "Centers", to: "/dashboard/lectures", icon: FaRegFileAlt },
        { label: "Groups", to: "/dashboard/groups", icon: AiOutlineGroup },
        { label: "Inactive", to: "/dashboard/inactive-students", icon: BsPersonX },
      ],
    },
    {
      title: "Curriculum",
      items: [
        { label: "Courses", to: "/dashboard/sections", icon: FaRegFileAlt },
        { label: "Lessons", to: "/dashboard/chapters", icon: FaBookOpen },
      ],
    },
    {
      title: "Content",
      items: [
        { label: "Sessions", to: "/dashboard/videos", icon: FaVideo },
        { label: "Live Sessions", to: "/dashboard/live-videos", icon: FaVideo },
        { label: "Office Hours", to: "/dashboard/office-hours", icon: MdAccessTime },
        { label: "Question Containers", to: "/dashboard/question-containers", icon: MdQuiz },
        { label: "Question Bank", to: "/dashboard/questions-bank", icon: MdQuiz },
        { label: "Exams", to: "/dashboard/exams", icon: MdOutlineAssignment },
        { label: "Homework", to: "/dashboard/homework", icon: MdOutlineAssignment },
        { label: "PDFs", to: "/dashboard/lessons", icon: FaFilePdf },
        { label: "Question Tags", to: "/dashboard/tags", icon: FaTags },
      ],
    },
    {
      title: "Finances",
      items: [
        { label: "Payments & Attendance", to: "/dashboard/payments", icon: LuDollarSign },
        { label: "Payment Subjects", to: "/dashboard/payment-subjects", icon: FaRegFileAlt },
      ],
    },
    {
      title: "Staff",
      items: [
        { label: "Teaching Assistants", to: "/dashboard/assistants", icon: BiPhone },
        { label: "Assistant Phones", to: "/dashboard/subject-assist-phones", icon: BiPhone },
      ],
    },
    {
      title: "Requests",
      items: [
        {
          label: "Exam Redo Requests",
          to: "/dashboard/exam-redo-requests",
          icon: FaQuestionCircle,
        },
        {
          label: "Video More View Requests",
          to: "/dashboard/videos-more-view-requests",
          icon: FaEyeSlash,
        },
      ],
    },
    {
      title: "Reports",
      items: [
        {
          label: "Review Homeworks",
          to: "/dashboard/uncomment-homework-report",
          icon: MdOutlineAssessment,
        },
        {
          label: "Live video attendance",
          to: "/dashboard/live-video-attendance-report",
          icon: FaVideo,
        },
        {
          label: "Detailed exam report",
          to: "/dashboard/detailed-exam-report",
          icon: MdQuiz,
        },
        {
          label: "Print Questions",
          to: "/dashboard/print-all-questions",
          icon: RiPrinterFill,
        },
      ],
    },
  ];

  return (
    <Flex
      as="aside"
      position={embedded ? "relative" : "fixed"}
      left={embedded ? undefined : "0"}
      top={embedded ? undefined : "0"}
      h={embedded ? "100%" : "100vh"}
      minH={embedded ? "min(100vh, 720px)" : undefined}
      w={embedded ? "100%" : "260px"}
      maxW={embedded ? "100%" : undefined}
      bg={palette.primary}
      borderRight="1px solid"
      borderColor={palette.tertiary}
      flexDirection="column"
      zIndex={embedded ? 1 : "40"}
      overflow="hidden"
    >
      <Box px={6} py={8}>
        <Text
          fontSize="xl"
          fontWeight="800"
          color="white"
          letterSpacing="tight"
          textTransform="uppercase"
        >
          Academic Editorial
        </Text>
        <Text
          fontSize="10px"
          color="rgba(255,255,255,0.6)"
          letterSpacing="0.18em"
          textTransform="uppercase"
          mt={1}
        >
          Admin System
        </Text>
      </Box>

      {isLoading ? (
        <HStack w="100%" bg="transparent" h="100%">
          <SkeletonText
            startColor="gray.100"
            endColor="gray.600"
            w="80%"
            mt="4"
            noOfLines={7}
            spacing="20"
          />
        </HStack>
      ) : (
        <Box
          flex="1"
          overflowY="auto"
          px={4}
          pb={20}
          className="scroll"
          sx={{
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE/Edge legacy
            "&::-webkit-scrollbar": {
              display: "none", // Chrome/Safari
            },
          }}
        >
          {sections.map((section) => (
            <Box key={section.title} mb={6}>
              <Text
                px={2}
                mb={2}
                fontSize="xs"
                color="rgba(255,255,255,0.5)"
                textTransform="uppercase"
                letterSpacing="0.22em"
                fontWeight="700"
                textAlign="left"
              >
                {section.title}
              </Text>
              <Box>
                {section.items.map((item) => {
                  const active = location.pathname === item.to;
                  const IconCmp = item.icon;
                  return (
                    <Link key={item.to} to={item.to} style={{ textDecoration: "none" }}>
                      <Flex
                        align="center"
                        justify="flex-start"
                        gap={3}
                        px={3}
                        py={2}
                        bg={active ? palette.secondary : "transparent"}
                        color={active ? "white" : palette.neutralText}
                        borderLeft={active ? "4px solid" : "4px solid transparent"}
                        borderColor={active ? palette.tertiary : "transparent"}
                        borderRadius="14px"
                        transition="all 150ms ease"
                        _hover={{
                          bg: active ? palette.secondary : "rgba(69, 11, 17, 0.5)",
                          color: "white",
                        }}
                        fontSize="sm"
                        fontWeight="600"
                      >
                        <Box as={IconCmp} fontSize="18px" />
                        <Text flex="1" textAlign="left">
                          {item.label}
                        </Text>
                      </Flex>
                    </Link>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Flex p={4} bg={palette.secondary} align="center" gap={3} justify="space-between">
        <Flex align="center" gap={3} minW={0} flex="1">
          <Flex
            w="40px"
            h="40px"
            flexShrink={0}
            bg={palette.primary}
            border="1px solid"
            borderColor={palette.tertiary}
            align="center"
            justify="center"
            borderRadius="12px"
          >
            <Box as={FaUserCircle} color="white" fontSize="20px" />
          </Flex>
          <Box overflow="hidden">
            <Text color="white" fontSize="sm" fontWeight="700" noOfLines={1}>
              Admin Profile
            </Text>
          </Box>
        </Flex>

        <AdminNotificationsBell />
        <ThemeToggleButton />
        <Popover placement="top-end" closeOnBlur>
          <PopoverTrigger>
            <IconButton
              aria-label="Account settings"
              icon={<RiSettings4Line size={22} />}
              variant="ghost"
              color="white"
              flexShrink={0}
              borderRadius="12px"
              _hover={{ bg: "rgba(255,255,255,0.12)" }}
              _active={{ bg: "rgba(255,255,255,0.18)" }}
            />
          </PopoverTrigger>
          <PopoverContent
            w="220px"
            border="1px solid"
            borderColor={palette.tertiary}
            bg="var(--surface-raised)"
            boxShadow="lg"
            _focus={{ outline: "none" }}
          >
            <PopoverArrow bg="var(--surface-raised)" borderColor={palette.tertiary} />
            <PopoverBody p={3}>
              <Button
                w="100%"
                justifyContent="flex-start"
                leftIcon={<RiLogoutBoxRLine size={20} />}
                bg={palette.primary}
                color="white"
                size="sm"
                fontWeight="700"
                borderRadius="lg"
                _hover={{ bg: palette.secondary }}
                onClick={() => dispatch(logoutUserAction())}
              >
                Log out
              </Button>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>
    </Flex>
  );
};

export default TeacherSidenavbar;
